<script lang="ts">
	import Banner from "@components/Banner.svelte";
	import OrganizerCard from "@components/OrganizerCard.svelte";
	import Icon from "@components/common/Icon.svelte";
	import LabelledInput from "@components/form/LabelledInput.svelte";
	import LabelledSelect from "@components/form/LabelledSelect.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import { getTranslate } from "@utils/translate.js";
	import paid from "@core/assets/icons/paid.svg?raw";
	import topped from "@core/assets/icons/topped.svg?raw";
	import type { CompetitionCategoryType } from "@core/schema/CompetitionCategoryType.js";
	import type { AnonymizedHandler } from "@core/schema/Handler.js";
	import { breedNameMap } from "@core/schema/names/breedNameMap.js";
	import { competitionCategoryTypeNameMap } from "@core/schema/names/competitionCategoryTypeNameMap.js";
	import { countryNameMap } from "@core/schema/names/countryNameMap.js";
	import { createSortApplication } from "@core/schema/utils/sortApplication.js";
	import { countryEmojiMap } from "@core/utils/countryEmojiMap.js";
	import { isBreed } from "@core/utils/isBreed.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import translations from "./Applications.translations.json" with { type: "json" };
	import type { ApplicationsProps } from "./ApplicationsProps.js";

	const { groupedApplications, applications }: ApplicationsProps = $props();
	const { tenant, competition } = getGlobalTemplateProps();
	const translate = getTranslate(translations);

	let filteredCompetitionCategoryType = $state<CompetitionCategoryType | null>(null);
	let nameFilter = $state("");

	const sortApplication = $derived(createSortApplication(filteredCompetitionCategoryType));

	function normalizeName(value: string): string {
		return value
			.trim()
			.toLowerCase()
			.normalize("NFD")
			.replace(/\p{Diacritic}/gu, "");
	}

	const normalizedNameFilter = $derived(normalizeName(nameFilter));

	function matchesNameFilter(handler: AnonymizedHandler | undefined): boolean {
		if (normalizedNameFilter === "") {
			return true;
		}
		if (!handler) {
			return false;
		}
		return normalizeName(`${handler.firstName} ${handler.lastName}`).includes(normalizedNameFilter);
	}

	// Rows for the two views (all teams vs. a single discipline). The ordinal `#`
	// is assigned on the FULL list (before the name search) so filtering by name
	// hides rows but never renumbers them — a row that is #9 without a filter stays
	// #9 even when it is the only search match.
	const groupedEntries = $derived(
		Object.entries(groupedApplications)
			.map(([key, value], index) => ({ key, value, ordinal: index + 1 }))
			.filter(({ value }) => matchesNameFilter(value.handler)),
	);
	const disciplineApplications = $derived(
		filteredCompetitionCategoryType === null
			? []
			: applications
					.filter((application) => application.categories.some(({ type }) => type === filteredCompetitionCategoryType))
					.toSorted(sortApplication)
					.map((application, index) => ({ application, ordinal: index + 1 }))
					.filter(({ application }) =>
						matchesNameFilter(groupedApplications[`${application.handlerId}:${application.dogId}`]?.handler),
					),
	);
	const visibleCount = $derived(
		filteredCompetitionCategoryType === null ? groupedEntries.length : disciplineApplications.length,
	);
	const columnCount = $derived(filteredCompetitionCategoryType === null ? 6 : 5);

	// Shared cell shapes for the leaderboard table.
	const thClass =
		"whitespace-nowrap px-4 py-3.5 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-white/80 sm:px-5";
	const tdClass = "px-4 py-3 text-sm sm:px-5";

	/* eslint-disable svelte/no-at-html-tags */
	/* eslint-disable @typescript-eslint/no-confusing-void-expression */
</script>

{#snippet paidIcon(mode: "unpaid" | "partial" | "paid")}
	<span
		class="inline-flex shrink-0"
		title={mode === "paid" ? translate("paid") : mode === "partial" ? translate("partiallyPaid") : translate("unpaid")}
	>
		<Icon
			icon={paid}
			class={[
				"w-4",
				{
					"text-grass-500": mode === "paid",
					"text-amber-500": mode === "partial",
					"text-primary": mode === "unpaid",
				},
			]}
		/>
	</span>
{/snippet}

{#snippet toppedIcon()}
	<span class="text-secondary inline-flex shrink-0" title={translate("topped")}>
		<Icon icon={topped} class="w-4" />
	</span>
{/snippet}

<Layout noFooterMargin>
	{#snippet customMain()}
		<main class="grid-container grow pt-4 sm:pt-6">
			{#if competition}
				<Banner {competition} />
				<OrganizerCard {competition} class="mb-10" />
			{/if}

			<section class="pb-16 sm:pb-20">
				<!-- Section head — accent tick + title + live count -->
				<div class="mb-6 flex flex-wrap items-baseline gap-3">
					<span class="bg-secondary h-7 w-1.5 shrink-0 self-center rounded-full" aria-hidden="true"></span>
					<h2 class="!mb-0">{translate("headline")}</h2>
					<span
						class="tabular text-ink-600 text-lg"
						aria-label={translate("competitorsCount", { count: visibleCount })}
					>
						{visibleCount}
					</span>
				</div>

				<!-- Filters -->
				<div class="mb-5 flex flex-wrap items-end gap-4">
					<LabelledSelect bind:value={filteredCompetitionCategoryType} wrapperClass="w-full sm:w-56">
						<span>{translate("disciplines")}</span>
						{#snippet options()}
							<option value={null}>{translate("all")}</option>
							{#each competition?.categories ?? [] as { type } (type)}
								<option value={type}>{competitionCategoryTypeNameMap[type][tenant.locale]}</option>
							{/each}
						{/snippet}
					</LabelledSelect>
					<LabelledInput
						type="search"
						value={nameFilter}
						wrapperClass="w-full sm:w-64"
						placeholder={translate("searchNamePlaceholder")}
						oninput={(event: Event & { currentTarget: EventTarget & HTMLInputElement }): void => {
							nameFilter = event.currentTarget.value;
						}}
					>
						<span>{translate("searchName")}</span>
					</LabelledInput>
				</div>

				{#if visibleCount === 0}
					<div class="border-ink-300 rounded-lg border border-dashed bg-white/60 px-5 py-12 text-center">
						<p class="text-ink-500 text-sm">{translate(normalizedNameFilter ? "noMatches" : "nooneApplied")}</p>
					</div>
				{:else}
					<!-- Status legend -->
					<div class="text-ink-700 mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
						<span class="inline-flex items-center gap-1.5"
							><Icon icon={paid} class="text-grass-500 w-3.5" />{translate("paid")}</span
						>
						<span class="inline-flex items-center gap-1.5"
							><Icon icon={paid} class="text-amber-500 w-3.5" />{translate("partiallyPaid")}</span
						>
						<span class="inline-flex items-center gap-1.5"
							><Icon icon={paid} class="text-primary w-3.5" />{translate("unpaid")}</span
						>
						<span class="inline-flex items-center gap-1.5"
							><Icon icon={topped} class="text-secondary w-3.5" />{translate("topped")}</span
						>
					</div>

					<!-- Leaderboard -->
					<div class="border-ink-200 shadow-card overflow-hidden rounded-lg border bg-white">
						<div class="overflow-x-auto">
							<table class="w-full border-collapse text-left">
								<caption class="sr-only">{translate("tableCaption")}</caption>
								<thead>
									<tr class="bg-ink-950">
										<th scope="col" class={thClass}>#</th>
										<th scope="col" class={thClass}>{translate("homeland")}</th>
										<th scope="col" class={thClass}>{translate("fullName")}</th>
										<th scope="col" class={thClass}>{translate("dogCallName")}</th>
										<th scope="col" class={thClass}>{translate("breed")}</th>
										{#if filteredCompetitionCategoryType === null}
											<th scope="col" class={thClass}>{translate("disciplines")}</th>
										{/if}
									</tr>
								</thead>
								<tbody class="divide-ink-100 divide-y">
									{#if filteredCompetitionCategoryType !== null}
										{#each disciplineApplications as { application, ordinal }, index (application.id)}
											{@const groupedApplication = groupedApplications[`${application.handlerId}:${application.dogId}`]}
											{@const category = application.categories.find(
												(category) => category.type === filteredCompetitionCategoryType,
											)}
											{@const paid = application.categories.reduce((sum, { paid }) => sum + paid, 0)}
											{@const totalPrice = application.categories.reduce((sum, { price }) => sum + price, 0)}
											{#if category && groupedApplication}
												{@const { handler, dog } = groupedApplication}
												{@const limit = competition?.categories.find(({ type }) => type === category.type)?.limit}
												<tr class={["hover:bg-secondary-50 transition-colors", index % 2 === 1 && "bg-ink-50/60"]}>
													<td class={[tdClass, "tabular text-ink-500 whitespace-nowrap font-semibold"]}>{ordinal}</td>
													<td class={[tdClass, "text-ink-600 whitespace-nowrap"]}>
														<div class="flex items-center gap-2">
															<div class="w-6 shrink-0">{@html countryEmojiMap[handler.homeland]}</div>
															<span>{countryNameMap[handler.homeland][tenant.locale]}</span>
														</div>
													</td>
													<td class={[tdClass, "text-ink-900 whitespace-nowrap font-semibold"]}>
														<div class="flex items-center gap-2">
															{@render paidIcon(paid >= totalPrice ? "paid" : paid === 0 ? "unpaid" : "partial")}
															<span>{handler.firstName} {handler.lastName}</span>
															{#if category.topped}
																{@render toppedIcon()}
															{/if}
														</div>
													</td>
													<td class={[tdClass, "text-ink-700 whitespace-nowrap"]}>{dog.callName}</td>
													<td class={[tdClass, "text-ink-600 whitespace-nowrap"]}>
														{isBreed(dog.breed) ? breedNameMap[dog.breed][tenant.locale] : dog.breed}
													</td>
												</tr>
												{#if limit !== undefined && ordinal === limit}
													<tr aria-hidden="true">
														<td colspan={columnCount} class="p-0">
															<div class="bg-primary-50 flex items-center gap-3 px-4 py-1.5 sm:px-5">
																<span class="bg-primary/30 h-px flex-1"></span>
																<span
																	class="tabular text-primary-600 text-[11px] font-semibold uppercase tracking-wide"
																>
																	{translate("capacityLimit")} · {limit}
																</span>
																<span class="bg-primary/30 h-px flex-1"></span>
															</div>
														</td>
													</tr>
												{/if}
											{/if}
										{/each}
									{:else}
										<!-- TODO na seznamu všech disciplín seřadit rovněž od nejstaršího createdAt -->
										{#each groupedEntries as { key, value, ordinal }, index (key)}
											{@const { dog, handler, categories, applications } = value}
											{@const paid = applications.reduce(
												(sum, application) => sum + application.categories.reduce((sum, { paid }) => sum + paid, 0),
												0,
											)}
											{@const totalPrice = applications.reduce(
												(sum, application) => sum + application.categories.reduce((sum, { price }) => sum + price, 0),
												0,
											)}
											<tr class={["hover:bg-secondary-50 transition-colors", index % 2 === 1 && "bg-ink-50/60"]}>
												<td class={[tdClass, "tabular text-ink-500 whitespace-nowrap font-semibold"]}>{ordinal}</td>
												<td class={[tdClass, "text-ink-600 whitespace-nowrap"]}>
													<div class="flex items-center gap-2">
														<div class="w-6 shrink-0">{@html countryEmojiMap[handler.homeland]}</div>
														<span>{countryNameMap[handler.homeland][tenant.locale]}</span>
													</div>
												</td>
												<td class={[tdClass, "text-ink-900 whitespace-nowrap font-semibold"]}>
													<div class="flex items-center gap-2">
														{@render paidIcon(paid >= totalPrice ? "paid" : paid === 0 ? "unpaid" : "partial")}
														<span>{handler.firstName} {handler.lastName}</span>
													</div>
												</td>
												<td class={[tdClass, "text-ink-700 whitespace-nowrap"]}>{dog.callName}</td>
												<td class={[tdClass, "text-ink-600 whitespace-nowrap"]}>
													{isBreed(dog.breed) ? breedNameMap[dog.breed][tenant.locale] : dog.breed}
												</td>
												<td class={[tdClass, "text-ink-600 min-w-[13rem]"]}>
													{categories.map(({ type }) => competitionCategoryTypeNameMap[type][tenant.locale]).join(", ")}
												</td>
											</tr>
										{/each}
									{/if}
								</tbody>
							</table>
						</div>
					</div>
				{/if}
			</section>
		</main>
	{/snippet}
</Layout>
